import { QueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lizardDateSchema } from "../dates";
import { paginatedSchema } from "../pagination";

import { organisationSchema } from "./organisations";

export const monitoringNetworkSchema = z.object({
  uuid: z.string(),
  url: z.string(),
  name: z.string(),
  organisation: organisationSchema,
  access_modifier: z.string(),
  num_timeseries: z.number(),
  description: z.string(),
  created: lizardDateSchema,
  last_modified: lizardDateSchema.nullable(),
});

export type MonitoringNetwork = z.infer<typeof monitoringNetworkSchema>;

export const monitoringNetworkListSchema = paginatedSchema(monitoringNetworkSchema);
export type MonitoringNetworkList = z.infer<typeof monitoringNetworkListSchema>;

export function useMonitoringNetwork(uuid: string) {
  return useParsedQuery<MonitoringNetwork>(
    ["lizard-api-v4", "monitoringnetworks", uuid],
    () => fetchWithError(`/api/v4/monitoringnetworks/${uuid}/`),
    (json: unknown) => monitoringNetworkSchema.parse(json)
  );
}

export function useMonitoringNetworks(params = {}) {
  const url = combineUrlAndParams("/api/v4/monitoringnetworks/", params);
  return useParsedQuery<MonitoringNetworkList>(
    ["lizard-api-v4", "monitoringnetworks", url],
    () => fetchWithError(url),
    (json: unknown) => monitoringNetworkListSchema.parse(json)
  );
}

export function useMultipleMonitoringNetworks(uuids: string[]) {
  return useParsedQueries(
    uuids.map((uuid) => {
      return {
        queryKey: ["lizard-api-v4", "monitoringnetworks", uuid],
        fetchFunction: () => fetchWithError(`/api/v4/monitoringnetworks/${uuid}/`),
        parseFunction: (json: unknown) => monitoringNetworkSchema.parse(json),
      };
    })
  );
}

// Use this outside components, so the scenario can still
// be stored in cache.
export async function fetchAndCacheMonitoringNetwork(
  queryClient: QueryClient,
  uuid: string
): Promise<MonitoringNetwork | null> {
  try {
    const response = await fetchWithError(`/api/v4/monitoringnetworks/${uuid}/`);
    const monitoringNetwork = monitoringNetworkSchema.parse(response);

    queryClient.setQueryData(["lizard-api-v4", "monitoringnetworks", uuid], monitoringNetwork);
    return monitoringNetwork;
  } catch (e) {
    return null;
  }
}
